import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Menu, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { scrollToId } from "@/lib/icons";

const LINKS = [
  { label: "Home", to: "/" },
  { label: "About Us", to: "/about" },
  { label: "Services", to: "/services" },
  { label: "Industries", to: "/", hash: "industries" },
  { label: "Contact Us", to: "/contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const go = (e, link) => {
    if (!link.hash) return;
    e.preventDefault();
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId(link.hash), 600);
    } else {
      scrollToId(link.hash);
    }
    setOpen(false);
  };

  const isActive = (link) => !link.hash && location.pathname === link.to;

  return (
    <header
      data-testid="main-navbar"
      className={`fixed inset-x-0 top-0 z-50 border-b border-gold/20 transition-all duration-300 ${
        scrolled ? "bg-navy-900/95 shadow-lg shadow-black/25 backdrop-blur-md" : "bg-navy-900/85 backdrop-blur-sm"
      }`}
    >
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6 lg:px-10">
        <Link to="/" aria-label="SSS GROUPS home" data-testid="nav-logo-link">
          <Logo />
        </Link>

        <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
          {LINKS.map((link) => (
            <Link
              key={link.label}
              to={link.to}
              onClick={(e) => go(e, link)}
              data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`group relative text-sm font-medium tracking-wide transition-colors ${
                isActive(link) ? "text-gold" : "text-slate-200 hover:text-white"
              }`}
            >
              {link.label}
              <span
                className={`absolute -bottom-1.5 left-0 h-0.5 bg-gold transition-all duration-300 ${
                  isActive(link) ? "w-full" : "w-0 group-hover:w-full"
                }`}
              />
            </Link>
          ))}
          <Link
            to="/contact?quote=1"
            data-testid="nav-get-quote-btn"
            className="group flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-semibold text-navy-950 transition-all duration-300 hover:bg-gold-light hover:shadow-lg hover:shadow-gold/25"
          >
            Get a Quote
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
          </Link>
        </nav>

        <button
          data-testid="nav-mobile-menu-btn"
          className="flex h-11 w-11 items-center justify-center rounded-full border border-gold/40 text-gold lg:hidden"
          onClick={() => setOpen((o) => !o)}
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-gold/15 bg-navy-900/98 backdrop-blur-md lg:hidden"
            aria-label="Mobile"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col gap-1 px-6 py-5">
              {LINKS.map((link, i) => (
                <motion.div
                  key={link.label}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                >
                  <Link
                    to={link.to}
                    onClick={(e) => go(e, link)}
                    className={`block rounded-lg px-3 py-3 text-base font-medium ${
                      isActive(link) ? "bg-white/5 text-gold" : "text-slate-200 hover:bg-white/5"
                    }`}
                  >
                    {link.label}
                  </Link>
                </motion.div>
              ))}
              <Link
                to="/contact?quote=1"
                className="mt-3 flex items-center justify-center gap-2 rounded-full bg-gold px-5 py-3 text-sm font-semibold text-navy-950"
              >
                Get a Quote <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
